import { z } from 'zod'

/** The product feature an AI provider call was made for. */
export const aiUsageFeatureValues = [
  'event_with_cover',
  'event_start',
  'event_cover',
  'event_photo',
  'ai_text',
  'event_translation',
  'cover_image',
  'format',
  'refine',
  'menu_scan',
  'plan_assistant',
  'place_from_website',
  'search_embeddings',
  'item_sorting',
] as const
export type AiUsageFeature = (typeof aiUsageFeatureValues)[number]

const count = () => z.number().int().nonnegative()
const usd = () => z.number().nonnegative()

const aiCostTotalsSchema = z.object({
  /** Provider calls, failed ones included. */
  calls: count(),
  failedCalls: count(),
  /** Calls whose model has no known price; their cost is missing from `costUsd`. */
  unpricedCalls: count(),
  /** Input tokens of every kind: text, image, cached and cache writes. */
  inputTokens: count(),
  outputTokens: count(),
  /** What the provider calls cost at list prices. */
  costUsd: usd(),
  /** Credits businesses kept for these operations; refunds are left out. */
  creditsCharged: count(),
  /** `costUsd` per credit charged, or null without credits. */
  usdPerCredit: z.number().nonnegative().nullable(),
})

/**
 * What AI features cost Pulsify at provider list prices, next to the credits businesses paid.
 * Months and days are calendar months and days in Europe/Vienna.
 */
export const adminAiCostsResponseSchema = z.object({
  generatedAt: z.string().datetime(),
  /** The current and the five previous months, newest first, one row per feature used. */
  monthly: z.array(
    aiCostTotalsSchema.extend({
      month: z.string().regex(/^\d{4}-\d{2}$/),
      feature: z.string(),
    }),
  ),
  /** The last 30 days, oldest first, ending today. */
  daily: z.array(aiCostTotalsSchema.extend({ day: z.string().date() })),
  /** The ten businesses whose AI calls cost the most in the last 30 days. */
  topBusinesses: z.array(
    aiCostTotalsSchema.extend({
      businessId: z.string().uuid(),
      name: z.string(),
    }),
  ),
})

export type AdminAiCostTotals = z.infer<typeof aiCostTotalsSchema>
export type AdminAiCostsResponse = z.infer<typeof adminAiCostsResponseSchema>
